// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import LoginPage from '@/pages/LoginPage'
import DashboardPage from '@/pages/DashboardPage'
import SchedulePage from '@/pages/SchedulePage'
import SettingsPage from '@/pages/SettingsPage'
import PatientsPage from '@/pages/PatientsPage'
import PatientDetailPage from '@/pages/PatientDetailPage'
import EncounterDetailPage from '@/pages/EncounterDetailPage'
import ProvidersPage from '@/pages/ProvidersPage'
import ProviderLayout from '@/components/ProviderLayout'
import PageTransition from '@/components/PageTransition'
import RequireAuth from '@/components/RequireAuth'
import RequireAdmin from '@/components/RequireAdmin'
import { ThemeProvider } from '@/context/theme-context'
import { PatientStoreProvider } from '@/context/patient-store-context'
import { AuthProvider } from '@/context/auth-context'

// Wraps a provider page in the route guard + authenticated layout.
// `adminOnly` additionally requires the admin custom claim.
function Protected({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const page = (
    <ProviderLayout>
      <PageTransition>{children}</PageTransition>
    </ProviderLayout>
  )
  return <RequireAuth>{adminOnly ? <RequireAdmin>{page}</RequireAdmin> : page}</RequireAuth>
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PatientStoreProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
              <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/schedule" element={<Protected><SchedulePage /></Protected>} />
              <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
              <Route path="/patients" element={<Protected><PatientsPage /></Protected>} />
              <Route path="/patients/:id" element={<Protected><PatientDetailPage /></Protected>} />
              <Route path="/patients/:id/encounters/:appointmentId" element={<Protected><EncounterDetailPage /></Protected>} />
              <Route path="/providers" element={<Protected adminOnly><ProvidersPage /></Protected>} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </PatientStoreProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
